import { FC, useMemo } from 'react';
import type { SelectOption } from '../types/select-option';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui-kit/dropdown-menu';
import { Icon } from '../ui-kit/icons';
import { cn } from '../ui-kit/utils';

interface TopHoldersFilterProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  contentClassName?: string;
  triggerClassName?: string;
}

export const TopHoldersFilter: FC<TopHoldersFilterProps> = ({
  options,
  value,
  onChange,
  contentClassName = '',
  triggerClassName = '',
}) => {
  const selectedLabel = useMemo(() => {
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption?.label ?? value ?? '';
  }, [options, value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={triggerClassName}>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-1 text-v2-text-secondary transition-colors
            hover:text-v2-text-primary outline-none"
        >
          <span className="text-[13px] leading-4 tracking-[-0.13px]">{selectedLabel}</span>
          <Icon name="ChevronDown" size={16} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={8}
        className={cn(
          'rounded-[10px] border border-v2-border bg-v2-surface-secondary p-1 shadow-v2-card',
          contentClassName,
        )}
      >
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => onChange(option.value)}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-4 rounded-[8px] px-3 py-2',
                'text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary outline-none transition-colors',
                'hover:bg-v2-btn-secondary focus:bg-v2-btn-secondary',
              )}
            >
              <span>{option.label}</span>
              {isSelected && <Icon name="Check" size={16} className="text-v2-text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
